import type { Dayjs } from 'dayjs'

// Snooze presets for the conversation actions menu. Labels are kept as i18n
// keys (not translated here) so they resolve live inside the component.
export type SnoozePresetKey =
  | 'oneHour'
  | 'threeHours'
  | 'tomorrow'
  | 'mondayNineAm'
  | 'nextWeek'

export const SNOOZE_PRESETS: { key: SnoozePresetKey; labelKey: string }[] = [
  { key: 'oneHour', labelKey: 'frontend:chatwoot.action.snoozeOneHour' },
  { key: 'threeHours', labelKey: 'frontend:chatwoot.action.snoozeThreeHours' },
  { key: 'tomorrow', labelKey: 'frontend:chatwoot.action.snoozeTomorrow' },
  { key: 'mondayNineAm', labelKey: 'frontend:chatwoot.action.snoozeMonday' },
  { key: 'nextWeek', labelKey: 'frontend:chatwoot.action.snoozeNextWeek' },
]

// Valid preset keys, for validating values read back from storage.
export const SNOOZE_PRESET_KEYS: readonly SnoozePresetKey[] =
  SNOOZE_PRESETS.map((preset) => preset.key)

export function isSnoozePresetKey(value: unknown): value is SnoozePresetKey {
  return (
    typeof value === 'string' &&
    (SNOOZE_PRESET_KEYS as readonly string[]).includes(value)
  )
}

// Chatwoot's toggle_status expects `snoozed_until` as a Unix timestamp in
// SECONDS, so always floor milliseconds down.
export function toUnixSeconds(value: Dayjs): number {
  return Math.floor(value.valueOf() / 1000)
}

// The next Monday at 09:00 local. When `now` is already Monday (any time), we
// jump to the FOLLOWING Monday so "Mo 9 Uhr" always moves the conversation to a
// fresh week rather than reopening later the same morning.
function nextMondayNineAm(now: Dayjs): Dayjs {
  const MONDAY = 1 // dayjs: 0 = Sunday … 1 = Monday
  const daysUntilMonday = (MONDAY - now.day() + 7) % 7 || 7
  return now
    .add(daysUntilMonday, 'day')
    .hour(9)
    .minute(0)
    .second(0)
    .millisecond(0)
}

// Resolve a preset to the absolute reopen time (Unix seconds), relative to
// `now`. Day-based presets land at 09:00 local so a conversation snoozed late
// in the day doesn't resurface at an odd hour.
export function snoozePresetUntil(key: SnoozePresetKey, now: Dayjs): number {
  switch (key) {
    case 'oneHour':
      return toUnixSeconds(now.add(1, 'hour'))
    case 'threeHours':
      return toUnixSeconds(now.add(3, 'hour'))
    case 'tomorrow':
      return toUnixSeconds(
        now.add(1, 'day').hour(9).minute(0).second(0).millisecond(0)
      )
    case 'mondayNineAm':
      return toUnixSeconds(nextMondayNineAm(now))
    case 'nextWeek':
      return toUnixSeconds(
        now.add(1, 'week').hour(9).minute(0).second(0).millisecond(0)
      )
  }
}
